import { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import Snackbar from "@mui/material/Snackbar";
import CloseIcon from "@mui/icons-material/Close";

const isStandalone = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
};

const TransitionUp = (props) => <Slide {...props} direction="up" />;

const PwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("OmnioFarm is ready to install.");

  useEffect(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return () => {};
    }

    if (!("serviceWorker" in navigator) || isStandalone()) {
      return () => {};
    }

    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const handleDisplayModeChange = (event) => {
      if (event.matches) {
        setOpen(false);
        setDeferredPrompt(null);
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleDisplayModeChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleDisplayModeChange);
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setMessage("Install OmnioFarm for quicker access and offline support.");
      setOpen(true);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setMessage("OmnioFarm is installed. Find it on your home screen.");
      setOpen(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleDisplayModeChange);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setMessage("Thanks! OmnioFarm will be installed.");
    } else {
      setMessage("No problem. You can install OmnioFarm later from the browser menu.");
    }
    setOpen(true);
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleClose = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleDismiss = () => setOpen(false);

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      TransitionComponent={TransitionUp}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      autoHideDuration={deferredPrompt ? null : 5000}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ alignItems: "center" }}
        action={
          <>
            {deferredPrompt ? (
              <Button
                color="inherit"
                size="small"
                onClick={handleInstall}
                sx={{
                  color: "rgba(255, 255, 255, 0.85)",
                  border: "1px solid rgba(255, 255, 255, 0.6)",
                  borderRadius: "999px",
                  px: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.9)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Install
              </Button>
            ) : null}
            <IconButton
              size="small"
              color="inherit"
              aria-label="dismiss install prompt"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default PwaInstallPrompt;
